import { autoinject, join, InlineViewStrategy } from 'aurelia-framework';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { httpRequest } from 'app/shared/services/http-service';

export type CustomViewReference = string | InlineViewStrategy;

function getProperty(target: any, path: string): any | undefined {
    if(!target) { return undefined; }
    const parts = path.split('.');
    let current = target;
    for(const part of parts) {
        if(current === undefined || current === null) {
            return undefined;
        }
        current = current[part];
    }
    return current;
}

@autoinject
export class CustomViewService {

    private views: any;
    private readonly resolvedViews = new Map<string, CustomViewReference>();

    constructor(private readonly env: EnvironmentService, private readonly logger: AppLogger) {
        this.logger.source = this;
    }

    public async resolveCustomViewUrls(baseUrl: string, key: string, tenant: string): Promise<CustomViewReference[]> {
        if(!this.views) {
            this.logger.info('Loading Custom View Configuration...');
            const viewsConfig = this.env.config.views.customConfiguration;
            if(viewsConfig) {
                const configUrl = this.env.resolveUrl(viewsConfig);
                const views = await httpRequest(configUrl, { format: 'json', cache: 'none' });
                if(views && typeof views === 'object') {
                    this.logger.info('Custom View Configuration Loaded.');
                    this.views = views;
                }
            }
            else {
                this.views = {};
            }
        }

        const results: CustomViewReference[] = [];
        const config = getProperty(this.views, key);
        if(config && typeof config === 'object') {
            const viewUrls = config[(tenant || '').toUpperCase()] || config['$default'];
            if(Array.isArray(viewUrls)) {
                for(const url of viewUrls) {
                    if(typeof url === 'string') {
                        const joinedUrl = baseUrl ? join(baseUrl, url) : url;
                        const resolved = await this.resolveCustomView(joinedUrl);
                        results.push(resolved);
                    }
                }
            }
        }

        return results;
    }

    /* Resolve a custom view either from cache or by fetching it with an HTTP request if webpack did not bundle it at build time.
       Allows users to specify custom views (in views-config.json) without needing to include them in the webpack bundle,
       as long as they are hosted somewhere that can be reached with an HTTP request. */
    private async resolveCustomView(viewUrl: string): Promise<CustomViewReference> {
        const cached = this.resolvedViews.get(viewUrl);
        if(cached) return cached;

        const runtimeUrl = this.env.resolveUrl(viewUrl);

        try {
            const templateMarkup = await httpRequest(runtimeUrl, { format: 'raw', cache: 'none' });
            if(typeof templateMarkup === 'string' && templateMarkup.trim().length > 0) {
                const inlineView = new InlineViewStrategy(templateMarkup);
                this.resolvedViews.set(viewUrl, inlineView);
                return inlineView;
            }
        } catch(error) {
            this.logger.debug('Custom runtime view not loaded; using template URL.', {
                viewUrl,
                runtimeUrl,
                error
            });
        }

        const bundledViewUrl = join('./', viewUrl);
        this.resolvedViews.set(viewUrl, bundledViewUrl);
        return bundledViewUrl;
    }

}
